import React, { useMemo } from "react";
import { Box, Typography, Button, useMediaQuery } from "@mui/material";
import { useSelector } from "react-redux";

import smallVendorHeroImage from "../../../assets/Images/small-vendors-hero.png";
import { Videos } from "../../../utils/Videos";

const SmallVendorHero = () => {
  const home = useSelector((state) => state.cms.home);

  const cmsHeroData = useMemo(() => {
    const sections = Array.isArray(home?.data) ? home.data : [];
    const heroSection = sections.find((item) => {
      if (!item) return false;
      const isActive = item.status === 1 || item.status === true;
      if (!isActive) return false;
      const isHeroSection = String(item.section || "").toLowerCase() === "hero";
      const isHomePage = String(item.page || "").toLowerCase() === "home";
      const isSmallVendors = String(item.title || "").toLowerCase().includes("small vendors initiative");
      return isHeroSection && isHomePage && isSmallVendors;
    });

    const related = Array.isArray(heroSection?.relatedCMS)
      ? heroSection.relatedCMS.filter((r) => r?.status === 1 || r?.status === true)
      : [];

    return {
      subtitle: heroSection?.sub_title || "Empowering India's Small Street Vendors One Purchase At A Time",
      description: heroSection?.description || "Buy products from hardworking street vendors feature on our platform.",
      image: heroSection?.image || Videos.smallVendorHero || smallVendorHeroImage,
      stats:
        related.length > 0
          ? related.map((item) => ({
              value: item?.title || "",
              label: item?.sub_title || item?.stand_title || "",
            }))
          : [
              { value: "250+", label: "Vendors Supported" },
              { value: "45+", label: "NGO Partners" },
              { value: "₹15L+", label: "Products Distributed" },
              { value: "30+", label: "Cities Covered" },
            ],
    };
  }, [home]);

  const heroMedia = cmsHeroData.image;
  const isVideo = /\.(mp4|webm|ogg|mov)(\?|$)/i.test(heroMedia);
  const detailStats = cmsHeroData.stats;

  const isMediumScreen = useMediaQuery("(min-width: 900px) and (max-width: 1200px)");
  const isSmallScreen = useMediaQuery("(max-width: 899px)");
  const isMobile = useMediaQuery("(max-width: 600px)");

  const getResponsiveStyles = () => {
    if (isMobile) {
      return {
        container: { height: "203px" },
        contentContainer: {
          flexDirection: "row",
          justifyContent: "flex-start",
          alignItems: "flex-end",
          textAlign: "left",
        },
        leftSection: { maxWidth: "100%", textAlign: "left" },
        title: { fontSize: "18px", lineHeight: "1.4", letterSpacing: "-0.36px", marginBottom: "12px" },
        description: { fontSize: "10px", lineHeight: "15.7px", letterSpacing: "0px", marginBottom: "12px", maxWidth: "220px" },
        button: {
          width: "181px",
          height: "42px",
          borderRadius: "10px",
          paddingTop: "14px",
          paddingBottom: "14px",
          boxShadow: "0px 3.72px 0px 0px #141414",
          marginBottom: "0px",
        },
      };
    }

    if (isSmallScreen) {
      return {
        container: { height: "400px" },
        contentContainer: {
          flexDirection: "row",
          justifyContent: "flex-start",
          alignItems: "flex-end",
          textAlign: "left",
        },
        leftSection: { maxWidth: "100%", textAlign: "left" },
        title: { fontSize: "32px", lineHeight: "120%", marginBottom: "16px" },
        description: { fontSize: "14px", lineHeight: "24px", marginBottom: "20px" },
        button: { width: "181px", height: "44px", marginBottom: "0px" },
      };
    }

    if (isMediumScreen) {
      return {
        container: { height: "640px" },
        contentContainer: { flexDirection: "row", justifyContent: "space-between", alignItems: "flex-end" },
        leftSection: { maxWidth: "500px" },
        title: { fontSize: "50px", lineHeight: "120%", marginBottom: "24px" },
        description: { fontSize: "16px", lineHeight: "28px", marginBottom: "24px" },
        button: { width: "181px", height: "46px", marginBottom: "16px" },
      };
    }

    return {
      container: { height: "640px" },
      contentContainer: { flexDirection: "row", justifyContent: "space-between", alignItems: "flex-end" },
      leftSection: { maxWidth: "800px" },
      title: { fontSize: "72px", lineHeight: "120%", marginBottom: "30px", letterSpacing: "-1.44px" },
      description: { fontSize: "18px", lineHeight: "34px", marginBottom: "30px", letterSpacing: "-0.36px" },
      button: { width: "181px", height: "50px", marginBottom: "20px" },
    };
  };

  const styles = getResponsiveStyles();
  const handleExploreVendors = () => {
    const target = document.getElementById("featured-vendors-section");
    if (!target) return;
    target.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <Box>
      <Box
        sx={{
          width: isMobile ? "calc(100% - 32px)" : "94.44%",
          height: styles.container.height,
          borderRadius: isMobile ? "10px" : isSmallScreen ? "16px" : "36px",
          position: "relative",
          overflow: "hidden",
          margin: "0 auto",
          marginTop: isMobile ? "0px" : "20px",
          marginBottom: isSmallScreen ? "0px" : "12px",
        }}
      >
        {isVideo ? (
          <Box
            component="video"
            src={heroMedia}
            autoPlay
            muted
            loop
            playsInline
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              zIndex: 1,
            }}
          />
        ) : (
          <Box
            component="img"
            src={heroMedia}
            alt="Small vendors hero"
            loading="eager"
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              zIndex: 1,
            }}
          />
        )}

        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background:
              "linear-gradient(98.457deg, #141414 21.976%, rgba(20, 20, 20, 0) 60.714%)",
            zIndex: 2,
          }}
        />

        <Box
          sx={{
            position: "relative",
            zIndex: 3,
            height: "100%",
            display: "flex",
            ...styles.contentContainer,
            padding: isMobile ? "20px" : isSmallScreen ? "20px" : "40px 0px 0px 40px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              maxWidth: styles.leftSection.maxWidth,
              marginBottom: isMobile ? "0px" : "20px",
              textAlign: styles.leftSection.textAlign,
            }}
          >
            <Typography
              variant="h1"
              sx={{
                fontWeight: 700,
                fontSize: styles.title.fontSize,
                lineHeight: styles.title.lineHeight,
                letterSpacing: styles.title.letterSpacing,
                color: "#FFFFFF",
                marginBottom: "15px",
                verticalAlign: "middle",
                textTransform: "none",
                whiteSpace: isMobile ? "pre-wrap" : "normal",
              }}
            >
              {cmsHeroData.subtitle}
            </Typography>

            <Typography
              variant="body1"
              sx={{
                fontWeight: 400,
                fontSize: styles.description.fontSize,
                lineHeight: styles.description.lineHeight,
                letterSpacing: styles.description.letterSpacing,
                color: "rgba(255, 255, 255, 0.8)",
                marginBottom: styles.description.marginBottom,
                verticalAlign: "middle",
                maxWidth: isMobile ? styles.description.maxWidth : "580px",
              }}
            >
              {cmsHeroData.description}
            </Typography>

            <Button
              variant="contained"
              onClick={handleExploreVendors}
              sx={{
                width: styles.button.width,
                height: styles.button.height,
                borderRadius: "10px",
                paddingTop: "14px",
                paddingBottom: "14px",
                background: "#F04951",
                boxShadow: "0px 4px 0px 0px #141414",
                marginBottom: styles.button.marginBottom,
                alignSelf: "flex-start",
                "&:hover": {
                  background: "#F04951",
                },
              }}
            >
              <Box
                sx={{
                  fontWeight: 700,
                  fontSize: "14px",
                  lineHeight: "14px",
                  textAlign: "center",
                  color: "#FFFFFF",
                  textTransform: "capitalize",
                }}
              >
                Explore Vendors
              </Box>
            </Button>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          width: isMobile ? "calc(100% - 32px)" : "94.44%",
          maxWidth: "1360px",
          mx: "auto",
          mt: isSmallScreen ? "20px" : "30px",
          mb: isSmallScreen ? "32px" : "40px",
          display: "grid",
          gridTemplateColumns: isSmallScreen ? "repeat(2, minmax(0, 1fr))" : "repeat(4, minmax(0, 1fr))",
          rowGap: isSmallScreen ? "20px" : 0,
          columnGap: isSmallScreen ? "16px" : "24px",
        }}
      >
        {detailStats.map((item) => (
          <Box
            key={item.label}
            sx={{
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: isMobile ? "22px" : "28px",
                lineHeight: 1,
                letterSpacing: isMobile ? "-0.44px" : "-0.56px",
                color: "#1D1D1B",
                mb: "12px",
              }}
            >
              {item.value}
            </Typography>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: isMobile ? "14px" : "16px",
                lineHeight: 1,
                color: "#8A8A8A",
              }}
            >
              {item.label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default SmallVendorHero;
